/**
 * ConflictsSummaryPanel - Shows all unresolved data conflicts prominently
 * Allows users to resolve conflicts from a central location
 */

import { useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Check,
  FileText,
  Clock,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { DataConflict } from "@shared/discovery-types";

interface ConflictsSummaryPanelProps {
  conflicts: DataConflict[];
  onResolveConflict: (field: string, selectedIndex: number) => void;
  className?: string;
}

function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat("de-CH", {
    dateStyle: "short",
  }).format(new Date(timestamp));
}

export function ConflictsSummaryPanel({
  conflicts,
  onResolveConflict,
  className,
}: ConflictsSummaryPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedConflicts, setExpandedConflicts] = useState<Set<string>>(
    new Set()
  );

  const unresolvedConflicts = conflicts.filter(c => !c.resolved);
  const resolvedCount = conflicts.length - unresolvedConflicts.length;

  if (conflicts.length === 0) {
    return null;
  }

  const toggleConflictExpanded = (field: string) => {
    setExpandedConflicts(prev => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  return (
    <Card
      className={cn(
        "overflow-hidden border-[var(--warning)]/30",
        unresolvedConflicts.length === 0 && "border-[var(--success)]/30",
        className
      )}
    >
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <div
            className={cn(
              "flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors",
              unresolvedConflicts.length > 0
                ? "bg-[var(--warning)]/5"
                : "bg-[var(--success)]/5"
            )}
          >
            <div className="flex items-center gap-3">
              {unresolvedConflicts.length > 0 ? (
                <AlertTriangle className="h-5 w-5 text-[var(--warning)]" />
              ) : (
                <Check className="h-5 w-5 text-[var(--success)]" />
              )}
              <div>
                <h3 className="font-semibold">
                  {unresolvedConflicts.length > 0
                    ? `${unresolvedConflicts.length} Data Conflict${
                        unresolvedConflicts.length !== 1 ? "s" : ""
                      } Need Resolution`
                    : "All Conflicts Resolved"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {unresolvedConflicts.length > 0
                    ? "Different documents provided different values for the same field"
                    : `${resolvedCount} conflict${
                        resolvedCount !== 1 ? "s" : ""
                      } resolved`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {resolvedCount > 0 && unresolvedConflicts.length > 0 && (
                <Badge variant="outline" className="text-[var(--success)]">
                  {resolvedCount} resolved
                </Badge>
              )}
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="p-0">
            <div className="divide-y">
              {conflicts.map(conflict => {
                const isConflictExpanded = expandedConflicts.has(
                  conflict.field
                );

                return (
                  <div key={conflict.field} className="p-4">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleConflictExpanded(conflict.field)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "h-8 w-8 rounded-full flex items-center justify-center",
                            conflict.resolved
                              ? "bg-[var(--success)]/10"
                              : "bg-[var(--warning)]/10"
                          )}
                        >
                          {conflict.resolved ? (
                            <Check className="h-4 w-4 text-[var(--success)]" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium">
                            {conflict.fieldLabel}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {conflict.values.length} different values from{" "}
                            {conflict.values.length} sources
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {conflict.resolved && (
                          <Badge
                            variant="outline"
                            className="text-[var(--success)]"
                          >
                            Resolved
                          </Badge>
                        )}
                        {isConflictExpanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    {isConflictExpanded && (
                      <div className="mt-4 space-y-2 pl-11">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3">
                          Choose the correct value
                        </div>
                        {conflict.values.map((value, index) => (
                          <button
                            key={`${value.source}-${index}`}
                            type="button"
                            onClick={() =>
                              onResolveConflict(conflict.field, index)
                            }
                            className={cn(
                              "w-full p-3 rounded-lg border text-left transition-all hover:border-primary/50 hover:bg-primary/5",
                              index === conflict.selectedIndex &&
                                "border-primary bg-primary/5 ring-1 ring-primary/20"
                            )}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">
                                  {value.value}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3" />
                                    {value.source}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(value.date)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge
                                  variant={
                                    value.confidence >= 80
                                      ? "default"
                                      : value.confidence >= 60
                                        ? "secondary"
                                        : "outline"
                                  }
                                >
                                  {value.confidence}%
                                </Badge>
                                {index === conflict.selectedIndex && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                        <p className="text-xs text-muted-foreground pt-2">
                          Click to select. Most recent value is selected by
                          default.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
